import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Stack, Typography, TypographyProps } from '@mui/material';

import { RowStyle } from './styled';

export interface IBookingDetailsItem {
	label: string;
	value: string;
	/** Previous value, shown struck through next to `value` when it differs. */
	previousValue?: string;
}

export interface IBookingDetailsDropdownProps {
	title: string;
	titleVariant?: TypographyProps['variant'];
	/** Items to show in the dropdown list (label left, value right) */
	items: IBookingDetailsItem[];
	itemsGap?: number | string;
	onExpandedChange?: (isExpanded: boolean) => void;
	toggleTestId?: string;
	testId?: string;
}

export const BookingDetailsDropdown: FC<IBookingDetailsDropdownProps> = ({
	title,
	titleVariant = 'h4-sb',
	items,
	itemsGap = 1,
	onExpandedChange,
	toggleTestId,
	testId,
}) => {
	const { t } = useTranslation('common');
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<Stack data-testid={testId}>
			<RowStyle>
				<Typography variant={titleVariant}>{title}</Typography>
				<Typography
					variant="l1"
					color="solutions.600"
					onClick={() => {
						const next = !isExpanded;
						setIsExpanded(next);
						onExpandedChange?.(next);
					}}
					sx={{ cursor: 'pointer' }}
					data-testid={toggleTestId}
				>
					{isExpanded ? t('button.less') : t('button.details')}
				</Typography>
			</RowStyle>

			<Collapse in={isExpanded}>
				<Stack gap={itemsGap} mt={1.5}>
					{items.map((item, index) => (
						<RowStyle key={index}>
							<Typography variant="b2">{item.label}</Typography>
							<Stack direction="row" gap={1} alignItems="center">
								{item.previousValue && (
									<Typography
										variant="b2"
										color="solutions.600"
										sx={{ textDecoration: 'line-through' }}
									>
										{item.previousValue}
									</Typography>
								)}
								<Typography variant="b2">{item.value}</Typography>
							</Stack>
						</RowStyle>
					))}
				</Stack>
			</Collapse>
		</Stack>
	);
};
